import React, { useState, useEffect } from 'react';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  FormControl,
  FormControlLabel,
  Grid,
  Alert,
  TextField,
  FormLabel,
  RadioGroup,
  Radio,
  Box, Chip,
} from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from '../axios/api';
import { useSelector } from 'react-redux';
import CustomButton from '../componentsII/Buttons';

// Custom CSS for the DatePicker
const datePickerStyles = `
  .react-datepicker {
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-size: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .react-datepicker__month-container {
    float: none;
    width: 280px;
  }
  
  .react-datepicker__header {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    padding: 1rem;
  }
  
  .react-datepicker__month-wrapper {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    padding: 0.5rem;
  }
  
  .react-datepicker__month-text {
    margin: 0.5rem;
    padding: 0.5rem;
    width: auto !important;
    border-radius: 4px;
  }
  
  .react-datepicker__month-text:hover {
    background-color: #f0f0f0;
  }
  
  .react-datepicker__month-text--selected {
    background-color: #1976d2 !important;
    color: white !important;
  }
  
  .react-datepicker__year-wrapper {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    padding: 0.5rem;
    max-width: none;
  }
  
  .react-datepicker__year-text {
    padding: 0.5rem;
    width: auto !important;
    border-radius: 4px;
  }
  
  .react-datepicker__year-text:hover {
    background-color: #f0f0f0;
  }
  
  .react-datepicker__year-text--selected {
    background-color: #1976d2 !important;
    color: white !important;
  }
  
  .date-picker-input {
    width: 100%;
    padding: 0.50rem;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 1rem;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s ease;
  }
  
  .date-picker-input:hover {
    border-color: #1976d2;
  }
  
  .date-picker-input:focus {
    border-color: #1976d2;
    outline: none;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }

  .react-datepicker-popper {
    z-index: 9999 !important;
  }
`;

const schema = yup.object({
  start_date: yup.date().required('Start date is required'),
  end_date: yup.date()
    .required('End date is required')
    .min(
      yup.ref('start_date'),
      'End date must be later than start date'
    )
    .test(
      'not-same',
      'End date cannot be the same as start date',
      function (value) {
        const start = this.parent.start_date;
        if (!start || !value) return true;
        return start.getFullYear() !== value.getFullYear() ||
          start.getMonth() !== value.getMonth();
      }
    ),
  is_base_year: yup.boolean(),
  is_target_year: yup.boolean(),
  target_reduction_percentage: yup.number().when('is_target_year', {
    is: true,
    then: () => yup.string()
      .required('Target percentage is required')
      .test('valid-percentage', 'Percentage must be between 0 and 100', value => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num > 0 && num <= 100;
      })
  })
}).test('exclusive', 'Only one of Base Year or Target Year can be selected', function (values) {
  if (values.is_base_year && values.is_target_year) {
    return this.createError({
      path: 'is_target_year',
      message: 'Cannot select both Base Year and Target Year'
    });
  }
  return true;
});


const AddYearModal = ({ editData, onClose, onSuccess, availableYears }) => {
  const [error, setError] = useState('');
  const [overlappingYearError, setOverlappingYearError] = useState('');
  const user = useSelector((state) => state.user.user);
  const [count, setCount] = useState([]);
  const [existingYears, setExistingYears] = useState(availableYears);
  

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = datePickerStyles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await api.get(`/company/companyInfoCount/${user.company.id}`);
        setCount(response.data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchCount();
  }, [user.company.id]);


  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: editData?.id || 0,
      company_id: user?.company?.id || 0,
      user_id: user?.id || 0,
      start_date: editData ? new Date(editData.start_year, editData.start_year_month) : null,
      end_date: editData ? new Date(editData.end_year, editData.end_year_month) : null,
      is_base_year: editData?.is_base_year || false,
      is_target_year: editData?.is_target_year || false,
      target_reduction_percentage: editData?.target_reduction_percentage || 0,
      add_organization_data: 'yes'
    }
  });

  const isDateBeyondAvailableYears = () => {
    const startDate = watch('start_date');
    const endDate = watch('end_date');

    // If no data or dates are not selected, return false
    if (!count || count.length === 0 || !startDate || !endDate) return false;

    // Find the previous year's data
    const currentStartYear = startDate.getFullYear();
    const previousYearData = count.find(item =>
      item.start_year === currentStartYear - 1
    );

    // Return true if the selected dates are beyond the latest year in the data
    return count.every(year =>
      startDate.getFullYear() > year.start_year ||
      (startDate.getFullYear() === year.start_year &&
        startDate.getMonth() > 11) // December is month 11
    );
  };

  // Function to check if there is any overlap with existing years
  const checkForOverlap = () => {
    const startDate = watch('start_date');
    const endDate = watch('end_date');
    
    if (!startDate || !endDate || !existingYears || existingYears.length === 0) {
      setOverlappingYearError('');
      return false;
    }
    
    // Skip checking the current year when editing
    const currentYearId = editData?.id || 0;
    
    const startTimestamp = new Date(startDate.getFullYear(), startDate.getMonth()).getTime();
    const endTimestamp = new Date(endDate.getFullYear(), endDate.getMonth()).getTime();
    
    const overlappingYear = existingYears.find(year => {
      // Skip the current record if editing
      if (year.id === currentYearId) return false;
      
      const yearStartTimestamp = new Date(parseInt(year.start_year), parseInt(year.start_year_month)).getTime();
      const yearEndTimestamp = new Date(parseInt(year.end_year), parseInt(year.end_year_month)).getTime();
      
      // Check if there's any overlap
      return (
        (startTimestamp >= yearStartTimestamp && startTimestamp <= yearEndTimestamp) ||
        (endTimestamp >= yearStartTimestamp && endTimestamp <= yearEndTimestamp) ||
        (startTimestamp <= yearStartTimestamp && endTimestamp >= yearEndTimestamp)
      );
    });
    
    if (overlappingYear) {
      const overlapStart = new Date(parseInt(overlappingYear.start_year), parseInt(overlappingYear.start_year_month));
      const overlapEnd = new Date(parseInt(overlappingYear.end_year), parseInt(overlappingYear.end_year_month));
      const startMonthName = overlapStart.toLocaleString('default', { month: 'long' });
      const endMonthName = overlapEnd.toLocaleString('default', { month: 'long' });
      
      setOverlappingYearError(
        `This time period overlaps with an existing year: ${startMonthName} ${overlappingYear.start_year} to ${endMonthName} ${overlappingYear.end_year}`
      );
      return true;
    }
    
    setOverlappingYearError('');
    return false;
  };

  // Check for overlaps whenever date selection changes
  useEffect(() => {
    checkForOverlap();
  }, [watch('start_date'), watch('end_date')]);


  useEffect(() => {
    if (editData) {
      setValue('start_date', new Date(editData.start_year, editData.start_year_month));
      setValue('end_date', new Date(editData.end_year, editData.end_year_month));
      setValue('is_base_year', editData.is_base_year);
      setValue('is_target_year', editData.is_target_year);
      setValue('target_reduction_percentage', editData.target_reduction_percentage);
    }
  }, [editData, setValue]);

  const is_base_year = watch("is_base_year");
  const is_target_year = watch("is_target_year");

  const handleBaseYearChange = (event) => {
    const newValue = event.target.checked;
    setValue("is_base_year", newValue);
    if (newValue && is_target_year) {
      setValue("is_target_year", false);
    }
  };

  const handleTargetYearChange = (event) => {
    const newValue = event.target.checked;
    setValue("is_target_year", newValue);
    if (newValue && is_base_year) {
      setValue("is_base_year", false);
    }
    if (!newValue) {
      setValue("target_reduction_percentage", 0);
    }
  };

  const onSubmit = async (data) => {
    try {
      setError('');
      
      // Check for overlaps before submitting
      if (checkForOverlap()) {
        // Don't proceed if there's an overlap
        return;
      }

      const previousYearData = count.find(
        item => item.start_year === data.start_date.getFullYear() - 1
      );

      const formattedData = {
        ...data,
        start_year: data.start_date.getFullYear().toString(),
        start_year_month: data.start_date.getMonth().toString(),
        end_year: data.end_date.getFullYear().toString(),
        end_year_month: data.end_date.getMonth().toString(),
      };

      if (isDateBeyondAvailableYears() && data.add_organization_data === 'yes') {
        formattedData.previous_year_id = previousYearData ? previousYearData.year_id : null;
      } else {
        delete formattedData.add_organization_data;
      }

      const response = await api.post("/company/addupdatecompanydatayear", formattedData);
      if (response.data.status === "200") {
        reset();
        onSuccess();
        onClose();
      } else {
        onSuccess(false, response.data.message);
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      setError('An error occurred while saving the data');
      onSuccess(false, 'An error occurred while saving the data');
    }
  };

  const handleReset = () => {
    reset();
    setError('');
    setOverlappingYearError('');
  };

  return (
    <Card sx={{ maxWidth: 600, margin: 'auto' }}>
      <CardHeader
        title={
          <Typography variant="h5">{editData ? "Edit Year" : "Add Year"}</Typography>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {overlappingYearError && (
          <Alert severity="error" sx={{ mb: 2 }}>{overlappingYearError}</Alert>
        )}
        {errors.is_target_year?.message && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.is_target_year.message}</Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>

              <Controller
                name="start_date"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <DatePicker
                      selected={value}
                      onChange={(date) => {
                        onChange(date);
                        // Reset end date if it becomes invalid with the new start date
                        const currentEndDate = watch('end_date');
                        if (currentEndDate && 
                            (currentEndDate < date || 
                             (currentEndDate.getFullYear() === date.getFullYear() && 
                              currentEndDate.getMonth() === date.getMonth()))) {
                          setValue('end_date', null);
                        }
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      showFullMonthYearPicker
                      placeholderText="Select Start Date"
                      className="date-picker-input"
                      calendarClassName="year-month-picker"
                      portalId="root"
                      openToDate={value || new Date()}
                    />
                    {error && <span className="text-red-500 text-sm">{error.message}</span>}
                  </FormControl>
                )}
              />
            </Grid>


            <Grid item xs={6}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date<span className="text-red-500">*</span>
              </label>
              <Controller
                name="end_date"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <DatePicker
                      selected={value}
                      onChange={onChange}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      showFullMonthYearPicker
                      placeholderText="Select End Date"
                      className="date-picker-input"
                      calendarClassName="year-month-picker"
                      portalId="root"
                      openToDate={value || new Date()}
                      disabled={!watch('start_date')}
                      minDate={watch('start_date')}
                      filterDate={(date) => {
                        const startDate = watch('start_date');
                        if (!startDate) return true;

                        const oneYearLater = new Date(startDate);
                        oneYearLater.setFullYear(startDate.getFullYear() + 1);

                        return (
                          !(date.getFullYear() === startDate.getFullYear() && date.getMonth() === startDate.getMonth()) &&
                          date <= oneYearLater
                        );
                      }}
                    />
                    {error && <span className="text-red-500 text-sm">{error.message}</span>}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={is_base_year}
                    onChange={handleBaseYearChange}
                    color="primary"
                  />
                }
                label="Is This Base Year?"
                labelPlacement="start"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={is_target_year}
                    onChange={handleTargetYearChange}
                    color="primary"
                  />
                }
                label="Is This Target Year?"
                labelPlacement="start"
              />
            </Grid>

            {is_target_year && (
              <Grid item xs={6}>
                <Controller
                  name="target_reduction_percentage"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error}>
                      <TextField
                        size='small'
                        {...field}
                        label="Target %"
                        type="number"
                        error={!!error}
                        helperText={error?.message}
                      />
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {isDateBeyondAvailableYears() && (
              <Grid item xs={12}>
                <Controller
                  name="add_organization_data"
                  control={control}
                  render={({ field }) => {
                    const previousYearData = count.find(
                      item => item.start_year === watch('start_date')?.getFullYear() - 1
                    );
                    
                    if (!previousYearData) return null;
                    
                    return (
                      <FormControl fullWidth error={!!error}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Add Organization Data from {previousYearData.start_year}? <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Typography variant="caption" color="textSecondary">
                          You have {previousYearData.locations} locations, {previousYearData.vehicles} vehicles, and {previousYearData.equipments} pieces of equipment that can be added
                        </Typography>
                        <RadioGroup {...field} defaultValue="yes" name="radio-buttons-group">
                          <FormControlLabel
                            value="yes"
                            control={<Radio color='success' />}
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                Yes<Chip
                                  label="Recommended"
                                  size="small"
                                  sx={{
                                    fontSize: '0.75rem',
                                    borderColor: 'warning.light',
                                    bgcolor: '#f2ead3'
                                  }}
                                  variant="outlined"
                                />
                              </Box>
                            }
                          />
                          <FormControlLabel value="no" control={<Radio color='success' />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    );
                  }}
                />
              </Grid>
            )}


            <Grid item xs={12}>
              <div className="w-full flex justify-end gap-5 items-center">
                <CustomButton
                  className="bg-gray-500 hover:bg-gray-700 text-white"
                  click={handleReset}
                  text="Reset"
                />
                <CustomButton
                  className="bg-green-600 hover:bg-green-700 text-white"
                  type="submit"
                  text={`${editData ? "Update" : "Submit"}`}
                  disabled={!!overlappingYearError}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddYearModal;